import * as _ from 'underscore';

import { IGlob } from '../Glob';
import { config } from '../../ts/config';
import { FontField } from '../../../../_common/platform/ts/FontField';
import { ColorBoxField } from '../../../../_common/platform/ts/ColorBoxField';
import NumberField from '../../../../_common/platform/ts/NumberField';
import BooleanField from '../../../../_common/platform/ts/BooleanField';
import SelectField from '../../../../_common/platform/ts/SelectField';
import UrlField from '../../../../_common/platform/ts/UrlField';
import StringField from '../../../../_common/platform/ts/StringField';
import toolbarButtons from '../../../../pdc-platform/src/ts/themes/toolbarButtonsTheme';

import * as appTheme from '../../../../_common/platform/ts/appTheme';
import { validators } from '../../../../_common/platform/ts/validators';

const template = require('!!raw-loader!pdc-platform/src/workspaceTheme.template.css').default;

import { ApplyModes, ApplySwitcherStates } from '../../../../_common/platform/tsx/filtering/FilterUtilities';
import { themeVars } from './themeConstants';

template;

declare var Color: any;

const _init = [];

let _backgroundImageURL: string;

export function init(glob: IGlob) {
    if (_.indexOf(_init, glob) !== -1) {
        return;
    }

    appTheme.registerSections([
        {
            id: config.themeID,
            name: 'Workspace',
            themeType: 'Workspace',
            visibility: ['application', 'workspace']
        }
    ]);

    appTheme.registerGroups([
        {
            id: themeVars.workspaceThemeGroups.workspaceFont,
            name: 'Workspace Font',
            section: 'workspace'
        },
        {
            id: themeVars.workspaceThemeGroups.workspaceBackground,
            name: 'Workspace Background',
            section: 'workspace'
        },
        {
            id: themeVars.workspaceThemeGroups.applyMode,
            name: 'Apply Mode',
            section: 'workspace'
        },
        {
            id: themeVars.workspaceThemeGroups.unsavedContentPrompt,
            name: 'Unsaved Content Prompt',
            section: 'workspace'
        }
    ]);

    appTheme.registerVars([
        {
            id: themeVars.workspaceThemeGroups.workspaceFont,
            param: new FontField(
                glob,
                'Font Face',
                [],
                function (theme) {
                    return theme['#font-main'];
                },
                {}
            ),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.workspaceFont,
            deps: ['#font-main']
        },
        {
            id: 'workspace-font-size',
            param: new SelectField(
                glob,
                'Font Size',
                [],
                function (theme) {
                    return theme['font-main-size'];
                },
                {
                    values: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ],
                    display: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ]
                }
            ),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.workspaceFont,
            deps: ['font-main-size']
        },
        {
            id: 'workspace-font-weight',
            param: new SelectField(
                glob,
                'Font Weight',
                [],
                function (theme) {
                    return theme['font-main-weight'];
                },
                {
                    values: ['lighter', 'normal', 'bold', 'bolder'],
                    display: ['lighter', 'normal', 'bold', 'bolder']
                }
            ),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.workspaceFont,
            deps: ['font-main-weight']
        },
        {
            id: 'workspace-font-italic',
            param: new BooleanField(glob, 'Italic', [], function (theme) {
                return theme['font-main-italic'];
            }),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.workspaceFont,
            deps: ['font-main-italic']
        },

        {
            id: 'workspace-bg',
            param: new ColorBoxField(
                glob,
                'Workspace Background Color',
                [],
                function (theme) {
                    return Color(theme['body-bg']).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.workspaceBackground,
            deps: ['body-bg']
        },
        {
            id: themeVars.backgroundImage,
            param: new UrlField(glob, 'Workspace Background Image URL', [], _backgroundImageURL, {
                canBrowse: true,
                browseInstanceId: 'theme-widget-resources',
                onURLSet: (value: string) => {
                    _backgroundImageURL = value;
                }
            }),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.workspaceBackground
        },
        {
            id: 'workspace-bg-image-scale',
            param: new SelectField(glob, 'Background Image Scale', [], 'Tile', {
                values: ['Cover', 'Contain', 'Strech', 'Tile'],
                display: ['Cover', 'Contain', 'Strech', 'Tile']
            }),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.workspaceBackground
        },

        {
            id: 'workspace-apply-mode',
            param: new SelectField(glob, 'Apply Mode', [], ApplyModes.AUTO, {
                values: [ApplyModes.AUTO, ApplyModes.SEMI_AUTO, ApplyModes.MANUAL],
                display: [
                    'Auto - Data are retrieved automatically on Workspace Open, Click on Tab and Filter change',
                    'Semi - Apply must be pressed on filter change; but automatically on TAB shift and Workspace open',
                    'Manual - Apply must always be pressed to retrieve new data'
                ]
            }),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.applyMode
        },
        {
            id: 'apply-switcher-state',
            param: new SelectField(glob, 'Apply Switcher State', [], ApplySwitcherStates.OFF_BY_DEFAULT, {
                values: [
                    ApplySwitcherStates.OFF_BY_DEFAULT,
                    ApplySwitcherStates.ON_BY_DEFAULT,
                    ApplySwitcherStates.HIDDEN
                ],
                display: ['Unchecked By Default', 'Checked By Default', 'Hidden']
            }),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.applyMode
        },

        {
            id: themeVars.unsavedContentPromptTitle,
            param: new StringField(glob, 'Prompt Title for Unsaved Content', [], null, {
                allowNull: true,
                placeholder: config.UNSAVED_WORKSPACE_CHANGES_WILL_BE_LOST_TITLE
            }),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.unsavedContentPrompt
        },
        {
            id: themeVars.unsavedContentPromptMessage,
            param: new StringField(glob, 'Prompt Message for Unsaved Content', [], null, {
                allowNull: true,
                placeholder: config.UNSAVED_WORKSPACE_CHANGES_WILL_BE_LOST_MESSAGE
            }),
            section: 'workspace',
            group: themeVars.workspaceThemeGroups.unsavedContentPrompt
        },

        {
            id: themeVars.tabIcon,
            param: new UrlField(glob, 'Tab Icon URL', [], null, {
                canBrowse: true,
                browseInstanceId: 'theme-widget-resources'
            }),
            section: 'workspace'
        },

        {
            id: 'workspace-widgets-margin',
            param: new NumberField(
                glob,
                'Space Between Widgets',
                [validators.number.not_negative, validators.number.integer],
                2,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'workspace'
        },

        {
            id: 'workspace-padding',
            param: new NumberField(
                glob,
                'Workspace Padding',
                [validators.number.not_negative, validators.number.integer],
                0,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'workspace'
        },

        // Hide unless we will find PDF exporter better than PhantomJS
        // OE: 21/01/2020
        // {
        //     id: 'workspace-printable',
        //     param: new BooleanField(
        //         glob, 'Workspace is printable', [],
        //         true
        //     ),
        //     section: 'workspace'
        // },

        {
            id: themeVars.workspaceAutoupdateInterval,
            param: new NumberField(
                glob,
                'Auto Update Interval (can be null or from 10 to 21600 seconds)',
                [validators.number.range(10, 21600)],
                config.WORKSPACE_AUTO_UPDATE_DEFAULT_INTERVAL,
                {
                    integer: true,
                    allowNull: true
                }
            ),
            section: 'workspace'
        }
    ]);

    toolbarButtons(
        glob,
        config.themeID,
        null,
        themeVars.section_Buttons,
        themeVars.justifyButtons,
        themeVars.showResetButton,
        themeVars.resetButtonShowMode,
        themeVars.showApplyButton,
        themeVars.showRefreshButton,
        themeVars.refreshButtonShowMode,
        null,
        null,
        themeVars.showBackButton,
        themeVars.backButtonShowMode,
        themeVars.showFullScreenButton,
        themeVars.fullScreenButtonShowMode
    );

    appTheme.registerPreRender(function (input) {
        var vars = {};

        return _.extend(input, vars);
    });

    appTheme.registerTemplate(template, ['workspace']);

    _init.push(glob);
}
